import {
  PLATFORM_COST_CADENCE_LABELS,
  PLATFORM_COST_CATEGORY_LABELS,
  annualizedCents,
  formatIsoDate,
  formatUsdFromCents,
  type PlatformCostLedgerRow,
} from "@/lib/platform/platformCostLedger";

function cadenceDetail(row: PlatformCostLedgerRow): string {
  const amount = formatUsdFromCents(row.amount_cents);
  if (row.cadence === "monthly") return `${amount} / month`;
  if (row.cadence === "annual") {
    return `${amount} / year · ${formatUsdFromCents(annualizedCents(row) / 12)} / month`;
  }
  return `${amount} one-time`;
}

export function PlatformCostLedgerList(props: {
  title: string;
  asOf: string;
  rows: PlatformCostLedgerRow[];
  busy: boolean;
  onEdit: (row: PlatformCostLedgerRow) => void;
  onReplace: (row: PlatformCostLedgerRow) => void;
  onEnd: (row: PlatformCostLedgerRow) => void;
  onDelete: (row: PlatformCostLedgerRow) => void;
}) {
  if (props.rows.length === 0) return null;
  return (
    <div className="space-y-2">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-400">{props.title}</h3>
      <div className="divide-y divide-gray-100 overflow-hidden rounded-xl border border-gray-200 dark:divide-zinc-800 dark:border-zinc-700">
        {props.rows.map((row) => {
          const canChange = row.cadence !== "one_time" && row.ended_on == null;
          const canEnd =
            row.cadence !== "one_time" &&
            row.started_on <= props.asOf &&
            (row.ended_on == null || row.ended_on > props.asOf);
          return (
            <div key={row.id} className="space-y-2 bg-white px-4 py-3 dark:bg-zinc-900/40">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-medium text-gray-900 dark:text-zinc-100">
                    {row.vendor}
                    <span className="font-normal text-gray-500 dark:text-zinc-400"> — {row.product_name}</span>
                  </div>
                  <div className="mt-0.5 text-sm text-gray-700 dark:text-zinc-200">{cadenceDetail(row)}</div>
                  <div className="mt-0.5 text-xs text-gray-500 dark:text-zinc-400">
                    {PLATFORM_COST_CATEGORY_LABELS[row.category]} · {PLATFORM_COST_CADENCE_LABELS[row.cadence]} · since{" "}
                    {formatIsoDate(row.started_on)}
                    {row.ended_on ? ` · through ${formatIsoDate(row.ended_on)}` : ""}
                  </div>
                  {row.notes ? <p className="mt-1 text-xs text-gray-600 dark:text-zinc-300">{row.notes}</p> : null}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {canChange ? (
                    <button
                      type="button"
                      disabled={props.busy}
                      onClick={() => props.onReplace(row)}
                      className="rounded-lg border border-gray-300 bg-white px-2.5 py-1 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-60 dark:border-zinc-600 dark:bg-zinc-900 dark:text-zinc-200"
                    >
                      Change rate
                    </button>
                  ) : null}
                  {canEnd ? (
                    <button
                      type="button"
                      disabled={props.busy}
                      onClick={() => props.onEnd(row)}
                      className="rounded-lg border border-gray-300 bg-white px-2.5 py-1 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-60 dark:border-zinc-600 dark:bg-zinc-900 dark:text-zinc-200"
                    >
                      End
                    </button>
                  ) : null}
                  <button
                    type="button"
                    disabled={props.busy}
                    onClick={() => props.onEdit(row)}
                    className="rounded-lg border border-gray-300 bg-white px-2.5 py-1 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-60 dark:border-zinc-600 dark:bg-zinc-900 dark:text-zinc-200"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    disabled={props.busy}
                    onClick={() => props.onDelete(row)}
                    className="rounded-lg px-2.5 py-1 text-xs text-red-700 hover:bg-red-50 disabled:opacity-60 dark:text-red-300 dark:hover:bg-red-950/40"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
